<script lang="ts" setup>
import AlertMessage from "@/components/messages/AlertMessage.vue";
import type { TripMonitorItem } from "@/api/trips/dto/trip-monitor";
import { computed, onMounted, onUnmounted, ref } from "vue";
import dayjs, { type Dayjs } from "dayjs";
import { useDateTime } from "@/composables";
import { TripStatus } from "@/constants/trip";
import { FIVE_MINUTES_IN_SECONDS } from "@/constants/time";

const { hasTimePassed, formatCountdown } = useDateTime();

const props = defineProps<{
  newTrips: TripMonitorItem[];
  tripsLoadedData: Dayjs;
}>();

const emits = defineEmits<{
  (e: "refresh"): void;
}>();

const intervalId = ref<ReturnType<typeof setInterval> | null>(null);
const timePassed = ref<string>("");
const fiveMinutesFromNow = ref<Dayjs>(dayjs());

const activeNewTrips = computed(() => {
  return props.newTrips.filter((trip) => trip.status !== TripStatus.completed);
});

const completedNewTrips = computed(() => {
  return props.newTrips.filter((trip) => trip.status === TripStatus.completed);
});

function getFiveMinutesFromNow() {
  return dayjs().add(5, "minutes");
}

function refresh() {
  emits("refresh");
  fiveMinutesFromNow.value = getFiveMinutesFromNow();
}

function checkHowTimePassed() {
  if (intervalId.value) {
    clearInterval(intervalId.value);
  }

  fiveMinutesFromNow.value = getFiveMinutesFromNow();

  intervalId.value = setInterval(() => {
    if (hasTimePassed(props.tripsLoadedData, FIVE_MINUTES_IN_SECONDS)) {
      refresh();
      return;
    }

    timePassed.value = formatCountdown(fiveMinutesFromNow.value);
  }, 1000);
}

onMounted(() => {
  checkHowTimePassed();
});

onUnmounted(() => {
  if (intervalId.value) {
    clearInterval(intervalId.value);
  }

  intervalId.value = null;
});
</script>

<template>
  <div class="d-flex align-center">
    <alert-message
      v-if="activeNewTrips.length || completedNewTrips.length"
      bg-color="indigo-lighten-5"
      border-color="none"
      icon-color="indigo"
    >
      <div class="d-flex align-center text-grey-darken-2">
        <span v-if="activeNewTrips.length"> {{ activeNewTrips.length }} trips have been added</span>

        <span v-if="activeNewTrips.length && completedNewTrips.length" class="mr-1">,</span>

        <span v-if="completedNewTrips.length">{{ completedNewTrips.length }} trips have been completed.</span>
      </div>
    </alert-message>

    <v-btn
      class="font-size-12 text-uppercase font-weight-600 ml-3"
      color="primary"
      size="small"
      variant="outlined"
      width="130"
      @click="refresh"
      >Refresh, {{ timePassed }}
    </v-btn>
  </div>
</template>

<style lang="scss" scoped></style>
